import { useState, useEffect } from 'react';
import { Shield, Smartphone, Key, Copy, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface TwoFactorAuthProps {
  onSetupComplete?: () => void;
}

export const TwoFactorAuth: React.FC<TwoFactorAuthProps> = ({ onSetupComplete }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkTwoFactorStatus();
  }, []);

  const checkTwoFactorStatus = async () => {
    try {
      // For now, simulate 2FA status since the table doesn't exist yet
      setIsEnabled(false);
    } catch (error) {
      console.error('Error checking 2FA status:', error);
    }
  };

  const generateTwoFactorSetup = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Call edge function to generate 2FA setup
      const { data, error } = await supabase.functions.invoke('setup-2fa', {
        body: { user_id: user.id }
      });

      if (error) throw error;

      setSecret(data.secret);
      setQrCode(data.qr_code_url);
      setBackupCodes(data.backup_codes);
      setIsSetupOpen(true);
    } catch (error) {
      console.error('Error generating 2FA setup:', error);
      toast({
        title: "Setup Failed",
        description: "Failed to generate 2FA setup. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const verifyAndEnable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a 6-digit verification code.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Verify the code with edge function
      const { data, error } = await supabase.functions.invoke('verify-2fa', {
        body: { 
          user_id: user.id,
          token: verificationCode,
          secret: secret
        }
      });

      if (error) throw error;

      if (data.verified) {
        setIsEnabled(true);
        setIsSetupOpen(false);
        onSetupComplete?.();
        toast({
          title: "2FA Enabled",
          description: "Two-factor authentication has been successfully enabled."
        });
      } else {
        toast({
          title: "Invalid Code",
          description: "The verification code is incorrect. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      toast({
        title: "Verification Failed",
        description: "Failed to verify the code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const disable2FA = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase.functions.invoke('disable-2fa', {
        body: { user_id: user.id }
      });

      if (error) throw error;

      setIsEnabled(false);
      toast({
        title: "2FA Disabled",
        description: "Two-factor authentication has been disabled."
      });
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast({
        title: "Error",
        description: "Failed to disable 2FA. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, type: 'secret' | 'backup') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'secret') {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      } else {
        setCopiedBackup(true);
        setTimeout(() => setCopiedBackup(false), 2000);
      }
      toast({
        title: "Copied",
        description: `${type === 'secret' ? 'Secret key' : 'Backup codes'} copied to clipboard.`
      });
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy to clipboard.",
        variant: "destructive"
      });
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <Shield className="w-6 h-6 text-primary" />
        <div>
          <h3 className="text-lg font-semibold">Two-Factor Authentication</h3>
          <p className="text-sm text-muted-foreground">
            Add an extra layer of security to your account
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Status</p>
            <p className="text-sm text-muted-foreground">
              {isEnabled ? 'Enabled and protecting your account' : 'Not enabled'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-sm">{isEnabled ? 'Enabled' : 'Disabled'}</span>
          </div>
        </div>

        <div className="flex gap-2">
          {!isEnabled ? (
            <Dialog open={isSetupOpen} onOpenChange={setIsSetupOpen}>
              <DialogTrigger asChild>
                <Button onClick={generateTwoFactorSetup} disabled={isLoading}>
                  <Smartphone className="w-4 h-4 mr-2" />
                  Enable 2FA
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  <Alert>
                    <AlertCircle className="w-4 h-4" />
                    <AlertDescription>
                      Install an authenticator app like Google Authenticator or Authy before proceeding.
                    </AlertDescription>
                  </Alert>

                  {qrCode && (
                    <div className="text-center">
                      <img src={qrCode} alt="2FA QR Code" className="mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground mb-2">
                        Scan this QR code with your authenticator app
                      </p>
                    </div>
                  )}

                  {secret && (
                    <div>
                      <Label>Manual Entry Key</Label>
                      <div className="flex gap-2 mt-1">
                        <Input value={secret} readOnly className="font-mono text-xs" />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(secret, 'secret')}
                        >
                          {copiedSecret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Verification Code</Label>
                    <Input
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      maxLength={6}
                    />
                  </div>

                  {backupCodes.length > 0 && (
                    <div>
                      <Label>Backup Codes</Label>
                      <Alert>
                        <Key className="w-4 h-4" />
                        <AlertDescription>
                          Save these backup codes in a secure location. You can use them to access your account if you lose your authenticator device.
                        </AlertDescription>
                      </Alert>
                      <div className="mt-2 p-3 bg-muted rounded font-mono text-xs">
                        {backupCodes.map((code, index) => (
                          <div key={index}>{code}</div>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(backupCodes.join('\n'), 'backup')}
                        className="mt-2 w-full"
                      >
                        {copiedBackup ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        Copy Backup Codes
                      </Button>
                    </div>
                  )}

                  <Button 
                    onClick={verifyAndEnable2FA} 
                    disabled={isLoading || !verificationCode}
                    className="w-full"
                  >
                    {isLoading ? 'Verifying...' : 'Enable 2FA'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <Button variant="destructive" onClick={disable2FA} disabled={isLoading}>
              Disable 2FA
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};